import { useEffect, useMemo, useState, useRef, RefObject } from "react";
import { useLocation, useParams, useSearchParams } from "wouter";
import { FbaShipmentStatus, SyncJobStatus, TransportType } from "@boxem/core-api-schema";

import { cancelBatch, getBatch } from "@/api/fulfillment";
import { FbaBatch, getItemAssignedQuantity, useShipmentContext } from "@/contexts/ShipmentContext";
import { useErrorModalContext } from "@/contexts/ErrorModalContext";
import LoadingSpinner from "@/components/LoadingSpinner/LoadingSpinner";
import ConfirmModal from "@/components/ConfirmModal";
import Alert from "@/components/Alert";

import styles from "./FbaShipmentFlow.module.css";

import { getBatchStep } from "../../utils";
import AdvanceShipmentStepModal from "../../AdvanceShipmentStepModal/AdvanceShipmentStepModal";
import ChooseProductsPage from "../ChooseProductsPage";
import PrepAndBoxemPage from "../PrepAndBoxemPage";
import ConfirmShipmentsPage from "../ConfirmShipmentsPage";
import ReviewShipmentsPage from "../ReviewShipmentsPage";
import ReviewAndShipPage from "../ReviewAndShipPage";
import FbaUpdateShipmentDrawer from "../../FbaUpdateShipmentDrawer/FbaUpdateShipmentDrawer";
import PageNotFound from "@/app/PageNotFound";
import { oneTimeDragNDropInit, DraggingState } from "./DragNDrop";

const PAGES = {
  [TransportType.SPD]: [
    ChooseProductsPage,
    PrepAndBoxemPage,
    ConfirmShipmentsPage,
    ReviewShipmentsPage,
  ],
  [TransportType.LTL]: [
    ChooseProductsPage,
    ConfirmShipmentsPage,
    PrepAndBoxemPage,
    ReviewAndShipPage,
    ReviewShipmentsPage,
  ],
};

export type FbaPageProps = {
  batch: FbaBatch;
  continueButton: ContinueButtonState;
  onContinueClicked: () => void;
  onCancelClicked: () => void;
  onError: (e: any) => void;
  cloneShipment: (focusedShipmentId?: number) => void;
  shipmentDetails: () => void;
  draggingState: DraggingState;
};

export type FbaShipmentFlowProps = {
  draggingState: DraggingState;
};
export default function FbaShipmentFlow(props: FbaShipmentFlowProps) {
  const { draggingState } = props;
  const [location, navigate] = useLocation();
  const [query, setQuery] = useSearchParams();
  const { batchId: sBatchId } = useParams<{ batchId?: string }>();
  const batchId = parseInt(sBatchId);
  const { handleErrorResponseGenerically } = useErrorModalContext();
  const {
    currentBatch: batch,
    setCurrentBatch,
    selectedTransportationOptions,
    openCloneShipmentConfirmModal,
    advanceShipmentFlowStep,
  } = useShipmentContext();
  const [loading, setLoading] = useState(false);
  const [loadingFailed, setLoadingFailed] = useState(false);
  const [showContinueModal, setShowContinueModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [showShipmentDetailsModal, setShowShipmentDetailsModal] = useState(false);
  const [loadingContinueButtonRequest, setLoadingContinueButtonRequest] = useState(false);
  const continueDisabledReason = useMemo(() => (
    getContinueDisabledReason(batch, selectedTransportationOptions)
  ), [batch, selectedTransportationOptions]);
  const [returnHref, setReturnHref] = useState("/");

  useEffect(() => {
    if (query.get("return")) {
      setReturnHref(query.get("return"));
      query.delete("return");
      setQuery(query, { replace: true });
    }
  }, []);

  useEffect(() => {
    if (batch?.id !== batchId) {
      setLoading(true);
      getBatch(batchId)
        .then(setCurrentBatch)
        .catch(e => {
          setLoadingFailed(true);
          console.error(e);
        })
        .finally(() => setLoading(false));
    }
  }, [batchId]);

  if (loading) {
    return <div className={styles.spinner}><LoadingSpinner /></div>;
  } else if (loadingFailed) {
    return <PageNotFound resourceType="shipment"/>;
  }

  const maxStep = (batch?.shipmentType === TransportType.LTL) ? 4 : 3;
  const batchStep = Math.min(maxStep, getBatchStep(batch));
  const Page: (props: FbaPageProps) => React.ReactNode = (batch && (PAGES[batch?.shipmentType]?.[batchStep] ?? (() => <PageNotFound />))) ?? (() => null);

  return (
    <>
      <Page
        batch={batch}
        continueButton={{ disabled: loadingContinueButtonRequest, ...continueDisabledReason }}
        onContinueClicked={() => setShowContinueModal(true)}
        onCancelClicked={() => setShowCancelModal(true)}
        onError={handleErrorResponseGenerically}
        cloneShipment={openCloneShipmentConfirmModal}
        shipmentDetails={() => setShowShipmentDetailsModal(true)}
        draggingState={draggingState}
      />
      <AdvanceShipmentStepModal
        open={showContinueModal}
        loading={loadingContinueButtonRequest}
        closeOnOutsideClick={false}
        onClose={async confirmed => {
          if (!confirmed) {
            setShowContinueModal(false);
            setLoadingContinueButtonRequest(false);
          } else {
            setLoadingContinueButtonRequest(true);
            await advanceShipmentFlowStep();
            setQuery(new URLSearchParams);
            setShowContinueModal(false);
            setLoadingContinueButtonRequest(false);
          }
        }}
      />
      <ConfirmModal
        confirmButtonSentinment="danger"
        open={showCancelModal}
        onClose={async (confirmed) => {
          if (!confirmed) {
            setShowCancelModal(false);
            return;
          }
          setCancelLoading(true);
          try {
            const result = await cancelBatch(batch.id);
            setCurrentBatch(null);
            navigate(returnHref);
          } catch(errors) {
            handleErrorResponseGenerically(errors);
          } finally {
            setCancelLoading(false);
            setShowCancelModal(false);
          }
        }}
        title={batchStep >= 3 ? "Cancel all Shipments" : "Cancel Shipment"}
        loading={cancelLoading}
        body={
          <>
            {(batch?.shipmentType === TransportType.LTL && batchStep >= 2) && (
              <Alert
                type="warning"
                message={
                  <>
                    <p>
                      In some cases, LTL shipment cancellation is known to fail to cancel the pick-up appointment
                      via Amazon's API. This is an ongoing issue the Amazon team is working to rectify. In the
                      meantime, in addition to cancelling through Boxem, please view your shipment on seller
                      central and ensure the shipment is cancelled there as well, otherwise you may incur a pickup
                      fee/the scheduled pickup may still occur.
                    </p>
                    <a href={`https://sellercentral.amazon.com/gp/ssof/shipping-queue.html#fbashipment`} target="_blank">
                      View Recent Shipments on Seller Central
                    </a>
                    <p>Sorry for the inconvience.</p>
                  </>
                }
              />
            )}
            {batchStep >= 3 && (
              <Alert
                type="warning"
                message={
                  <p>
                    Cancelling all shipments will prevent you from proceeding with them.
                    {batch?.shipments.length > 1 && "This will cancel all shipments in your batch, not just the currently selected one."}
                    This action is not reversible.
                  </p>
                }
              />
            )}
            {batchStep < 3 && (
              <Alert
                type="warning"
                message={
                  <p>You will be unable to proceed with this shipment if you continue. This action is not reversible.</p>
                }
              />
            )}
          </>
        }
      />
      <FbaUpdateShipmentDrawer
        open={showShipmentDetailsModal}
        onClose={() => setShowShipmentDetailsModal(false)}
      />
    </>
  );
}

export type ContinueButtonState = { disabled: boolean, reason?: string };

function getContinueDisabledReason(batch: FbaBatch, selectedTransportationOptions: Record<number, string>): ContinueButtonState {
  if (!batch) return { disabled: true, reason: "No shipment loaded." };

  const stepNumber = getBatchStep(batch);

  const validatePrepAndBoxemPage: () => ContinueButtonState = () => {
    // in step 2, the button is disabled if you haven't satisfied all of your packing groups.
    // this is defined as all items having quantityAssigned === quantity and all boxes having dims&weight set
    if (!batch.boxes || batch.boxes.length === 0) {
      return {
        disabled: true,
        reason: "You need to assign all of your items in all packing groups first.",
      };
    }

    for (let i = 0; i < batch.boxes.length; i++) {
      const box = batch.boxes[i];
      if (!box.length || !box.width || !box.height || !box.weight) {
        return {
          disabled: true,
          reason: `You need to set the dimensions and weight of Box ${box.displayIndex} to continue.`
        };
      }

      if (box.packingItems.length < 1) {
        return {
          disabled: true,
          reason: `You need to assign items to Box ${box.displayIndex} or remove it to continue.`
        };
      }
    }

    const packingOption = batch.chosenPackingOption;
    if (!packingOption) {
      return {
        disabled: true,
        reason: "No packing option chosen",
      };
    }

    if (batch.shipmentType === TransportType.SPD) {
      for (const [i, pg] of packingOption.packingGroups.entries()) {
        for (const item of pg.items) {
          const assigned = getItemAssignedQuantity(batch, pg.inboundPackingGroupId, null, item.msku);
          if (assigned !== item.quantity) {
            return {
              disabled: true,
              reason: `Packing group ${i+1} must assign all of its items to boxes before continuing.`,
            }
          }
        }
      }
    }
    else if (batch.shipmentType === TransportType.LTL) {
      for (const shipment of batch.shipments) {
        if (shipment.pallets.length === 0) {
          return {
            disabled: true,
            reason: `You need to add at least one pallet to shipment ${shipment.shipmentName} to continue. (Switch between shipments with the selector in the top-left).`,
          };
        }
        if (!shipment.contactName || !shipment.contactPhone) {
          return {
            disabled: true,
            reason: `You need to add shipper information for contact name/contact phone in shipment ${shipment.shipmentName} to continue. (Switch between shipments with the selector in the top-left).`,
          };
        }

        for (const item of shipment.items) {
          const assigned = getItemAssignedQuantity(batch, null, shipment.id, item.sku);
          if (assigned !== item.quantity) {
            return {
              disabled: true,
              reason: `Shipment ${shipment.shipmentName} must assign all of its items to boxes before continuing.`,
            };
          }
        }
      }
    }
  
    return { disabled: false };
  }

  const validateConfirmShipmentsPage: () => ContinueButtonState = () => {
    if (batch.shipmentType === TransportType.LTL && !!batch.selectedPlacementOptionId) {
      // LTL just needs to select a placement option.
      return { disabled: false };
    }

    // we currently let you proceed as long as you have selected a placement option
    // that has fetched all of its transportation options (LTL can ignore transport options).
    let valid = false;
    let atLeastOnePlacementOptionIncomplete = false;
    let atLeastOnePlacementOptionNotExpired = false;
    for (const po of batch.placementOptions) {
      if (!po.isExpired) {
        atLeastOnePlacementOptionNotExpired = true;
      }
      let allPlacementOptionsFetched = true;
      for (const so of po.shipmentOptions) {
        if (!Array.isArray(so.transportationOptions) || so.transportationOptions.length === 0) {
          allPlacementOptionsFetched = false;
          atLeastOnePlacementOptionIncomplete = true;
        }
      }
      if (po.placementOptionId === batch.selectedPlacementOptionId) {
        valid = allPlacementOptionsFetched;
      }
    }

    if (!atLeastOnePlacementOptionNotExpired) {
      return {
        disabled: true,
        reason: `It appears like all of the placement options have expired. It's recommended to cancel your shipment.`
      };
    } else if (valid && atLeastOnePlacementOptionIncomplete) {
      return {
        disabled: true,
        reason: `We haven't finished fetching transportation options for every placement option yet. If this process is taking more than 5 minutes, try refreshing.`
      };
    } else if (!valid) {
      return {
        disabled: true,
        reason: `We're not done loading the transportation information for your placement options yet. If this process is taking more than 5 minutes, try refreshing.`,
      };
    }

    return { disabled: false };
  }

  const validateLTLTransportationPage: () => ContinueButtonState = () => {
    if (undefined !== batch.shipments.find(s => !selectedTransportationOptions?.[s.id])) {
      return {
        disabled: true,
        reason: `Select a shipping carrier and pickup date before continuing.`,
      };
    }
    return { disabled: false };
  }

  switch (stepNumber) {
    case 0: { // step 1
      if (batch.items.length === 0) {
        return {
          disabled: true,
          reason: "Search for and add at least one product to your shipment first",
        };
      } else {
        const itemWithoutCompletedPoll = batch.items.find(item => item.prepCategoryPollingStatus !== SyncJobStatus.COMPLETED);
        if (!!itemWithoutCompletedPoll) {
          return {
            disabled: true,
            reason: `We're setting the prep category for SKU: ${itemWithoutCompletedPoll.sku}. Hang tight, this typically only takes a minute.`
          };
        }
      }
      return { disabled: false };
    }
    case 1: { // step 2
      if (batch.shipmentType === TransportType.LTL) {
        return validateConfirmShipmentsPage();
      } else {
        return validatePrepAndBoxemPage();
      }
    }
    case 2: { // step 3 and onward
      if (batch.shipmentType === TransportType.LTL) {
        return validatePrepAndBoxemPage();
      } else {
        return validateConfirmShipmentsPage();
      }
    }
    default: {
      if (batch.shipmentType === TransportType.LTL) {
        return validateLTLTransportationPage();
      }
      return { disabled: true, reason: 'No further steps'};
    }
  }
}
